import { TUNING } from '@cloudpearl/config';
import type { MapDef } from '@cloudpearl/config';
import { Rng, STREAM, hash32 } from './rng';
import { ValueNoise } from './noise';
import { lerp, saturate, smooth3 } from './math';

/**
 * The open world: an infinite, seeded heightfield (x east, z south, north = −z).
 *
 * - Dunes: wind-swell ridges whose crest lines run east–west and meander, so heading north
 *   gives a rhythm of long rises, launch crests and steep landing faces — the hills are built to
 *   reward diving and timing, and going sideways means following ridges instead.
 * - Sky Isles: floating islands on a jittered grid, each with rolling swells and a raised rim
 *   that kicks you toward the next island. Between them is open sky.
 *
 * Only + − × ÷ sqrt floor and integer hashing are used, so every device computes identical
 * ground — required for deterministic, shareable runs.
 */

export interface Island {
  id: number;
  /** Which chain, and position along it (0 = southernmost). */
  chain: number;
  index: number;
  cx: number;
  cz: number;
  r: number;
  /** Unit heading toward the next island in the chain; the island is stretched along it. */
  ux: number;
  uz: number;
  /** Elongation along the heading (semi-axis = r·e): a longer runway to build speed. */
  e: number;
  top: number;
  depth: number;
  lip: number;
}

export interface GroundSample {
  h: number;
  /** ∂h/∂x, ∂h/∂z */
  hx: number;
  hz: number;
  /** 0..1 ground coverage (sky islands); always 1 on the dunes. */
  mask: number;
}

const EPS = 0.3;

export class World {
  readonly seed: number;
  readonly map: MapDef;
  readonly islands: boolean;

  private readonly n: ValueNoise;
  private readonly warpN: ValueNoise;
  private readonly chains = new Map<number, Island[]>();
  private readonly chainRng = new Map<number, Rng>();
  private readonly scratch: Island[] = [];
  private readonly out: GroundSample = { h: 0, hx: 0, hz: 0, mask: 0 };

  constructor(seed: number, map: MapDef) {
    this.seed = seed;
    this.map = map;
    this.islands = map.terrain.islands.enabled;
    this.n = new ValueNoise(seed, STREAM.profile);
    this.warpN = new ValueNoise(seed, STREAM.curve);
  }

  /** 0 → 1 over the difficulty distance (eased). */
  difficulty(progress: number): number {
    const t = saturate(progress / this.map.terrain.difficultyDistance);
    return t * (2 - t);
  }

  runSpeed(progress: number): number {
    return lerp(TUNING.runSpeedStart, TUNING.runSpeedEnd, saturate(progress / TUNING.runSpeedRampDistance));
  }

  // ---------------------------------------------------------------- swells

  /** Asymmetric periodic ridge: rises over [0, c), falls over [c, 1). Range 0..1, C¹. */
  private profile(t: number): number {
    const c = this.map.terrain.swell.crest;
    return t < c ? smooth3(t / c) : 1 - smooth3((t - c) / (1 - c));
  }

  /** Swell field (m). `amp` scales the crest height. */
  swell(x: number, z: number, amp: number): number {
    const sw = this.map.terrain.swell;
    const n = this.warpN;
    const p = -z;
    const wA = sw.warp * (n.n2(x / 230, z / 230) * 0.7 + n.n2(x / 97 + 11, z / 97 - 7) * 0.3);
    const pa = (p + wA) / sw.wavelengthA;
    const fa = this.profile(pa - Math.floor(pa));
    const wB = sw.warp * 0.8 * (n.n2(x / 180 + 40, z / 180 + 3) * 0.7 + n.n2(x / 71 - 9, z / 71 + 2) * 0.3);
    const pb = (p + wB + 17) / sw.wavelengthB;
    const fb = this.profile(pb - Math.floor(pb));
    const mixT = smooth3(saturate(n.n2(x / 420 + 3, z / 420 - 8) * 0.9 + 0.5));
    const f = fa + (fb - fa) * mixT;
    const d = this.difficulty(p);
    const lo = lerp(sw.amplitude[0], sw.amplitudeLate[0], d);
    const hi = lerp(sw.amplitude[1], sw.amplitudeLate[1], d);
    const a = lerp(lo, hi, saturate(this.n.n2(x / 310, z / 310) * 0.7 + 0.5)) * amp;
    return a * f + sw.crossAmplitude * this.n.n2(x / 38 + 5, z / 44 - 3) + sw.macroAmplitude * this.n.n2(x / 900, z / 900);
  }

  /**
   * A designed run-up on the starting island: you begin on a rise and roll downhill toward the
   * north lip, so even the first hop is earned by diving, not by luck.
   */
  private startRamp(x: number, z: number): number {
    const dz = z + 30;
    const radial = 1 - smooth3(saturate((Math.sqrt(x * x * 0.6 + dz * dz * 0.25) - 30) / 30));
    if (radial <= 0) return 0;
    const t = saturate((8 - z) / 72);
    return 11 * (1 - smooth3(t)) * radial;
  }

  /** Flatten the land around the start so every run begins calmly. */
  private startCalm(x: number, z: number): number {
    const r = this.map.terrain.startRadius;
    const d2 = x * x + z * z;
    const t = saturate((Math.sqrt(d2) - r * 0.35) / r);
    return 0.12 + 0.88 * smooth3(t);
  }

  // ---------------------------------------------------------------- islands
  //
  // Islands come in meandering north-running chains, ~chainSpacing apart. Consecutive islands in
  // a chain are a bounded hop apart (measured coast to coast through the real, warped coastline)
  // and the next island is never much higher than the last. Chain 0 starts on the start island;
  // every other chain starts as a *branch* — one hop off the side of an island in the chain next
  // in toward the start — so the whole archipelago is connected: every island you can see can be
  // jumped to. Neighbouring chains drift together and apart, so there are real choices.

  private chainList(k: number): Island[] {
    let list = this.chains.get(k);
    if (!list) {
      const rng = new Rng(this.seed, STREAM.skyIslands, hash32(k, 1));
      this.chainRng.set(k, rng);
      // The start island: round, generous, pointing north. Any other chain forks off its
      // inward neighbour.
      const first = k === 0 ? this.makeIsland(0, 0, 0, -12, 74, this.topField(0, -12), rng, 0, -1, 1) : this.branchIsland(k, rng);
      list = [first];
      this.chains.set(k, list);
    }
    return list;
  }

  /**
   * The first island of chain k (k ≠ 0): a fork off the 2nd–4th island of chain k∓1, angled out
   * toward chain k's lane and one hop away.
   */
  private branchIsland(k: number, rng: Rng): Island {
    const side = k > 0 ? 1 : -1;
    const inner = k - side;
    const at = 1 + rng.int(0, 2);
    const parent = this.chainUpTo(inner, at)[at]!;
    // Rotate the parent's heading out toward our side by atan(0.85..1.4) (≈40–55°).
    const t = rng.range(0.85, 1.4);
    const c = 1 / Math.sqrt(1 + t * t);
    const sn = t * c;
    let hx = parent.ux * c + side * -parent.uz * sn;
    let hz = parent.uz * c + side * parent.ux * sn;
    // Always onward (north-ish), never back.
    if (hz > -0.35) {
      hz = -0.35;
      const l = Math.sqrt(hx * hx + hz * hz);
      hx /= l;
      hz /= l;
    }
    return this.hopFrom(k, 0, parent, hx, hz, rng);
  }

  /**
   * A new island one hop from `from` along (hx, hz): random size/shape for this far north,
   * heading on toward its chain's lane, placed so the real coast-to-coast gap is the chosen one.
   */
  private hopFrom(k: number, index: number, from: Island, hx: number, hz: number, rng: Rng): Island {
    const def = this.map.terrain.islands;
    const d = this.difficulty(-from.cz);
    const r = rng.range(lerp(def.radius[0], def.radiusLate[0], d), lerp(def.radius[1], def.radiusLate[1], d));
    const gap = rng.range(lerp(def.gap[0], def.gapLate[0], d), lerp(def.gap[1], def.gapLate[1], d));
    const e = rng.range(def.elongation[0], def.elongation[1]);
    const h = this.pickHeading(k, from.cx + hx * (this.islandExtent(from, hx, hz) + gap + r), rng);
    const probe: Island = { id: 0, chain: k, index: 0, cx: 0, cz: 0, r, ux: h.ux, uz: h.uz, e, top: 0, depth: 0, lip: 0 };
    let step = this.islandExtent(from, hx, hz) + gap + this.islandExtent(probe, -hx, -hz);
    const isl = this.makeIsland(k, index, from.cx + hx * step, from.cz + hz * step, r, 0, rng, h.ux, h.uz, e);
    // The coastlines are warped: nudge along the hop until the real gap is the one we chose.
    for (let i = 0; i < 3; i++) {
      const err = gap - this.coastGap(from, isl);
      if (err > -0.5 && err < 0.5) break;
      step += err;
      isl.cx = from.cx + hx * step;
      isl.cz = from.cz + hz * step;
    }
    isl.top = Math.min(this.topField(isl.cx, isl.cz) + (rng.float() - 0.5) * 1.2, from.top + 1);
    return isl;
  }

  /** Chain k with at least `index` + 1 islands. */
  private chainUpTo(k: number, index: number): Island[] {
    const list = this.chainList(k);
    while (list.length <= index) this.extendChain(k, list);
    return list;
  }

  /** The next island straight down the last one's runway. */
  private extendChain(k: number, list: Island[]): void {
    const last = list[list.length - 1]!;
    list.push(this.hopFrom(k, list.length, last, last.ux, last.uz, this.chainRng.get(k)!));
  }

  private makeIsland(k: number, index: number, cx: number, cz: number, r: number, top: number, rng: Rng, ux: number, uz: number, e: number): Island {
    const def = this.map.terrain.islands;
    return { id: hash32(k, index, 77), chain: k, index, cx, cz, r, ux, uz, e, top, depth: r * rng.range(def.depth[0], def.depth[1]), lip: def.lip * rng.range(0.8, 1.2) };
  }

  /** Outgoing heading for an island at cx: mostly north, wandering, pulled back to its lane. */
  private pickHeading(k: number, cx: number, rng: Rng): { ux: number; uz: number } {
    const def = this.map.terrain.islands;
    const lane = (k * def.chainSpacing - cx) / def.chainSpacing;
    let t = rng.range(-def.turn, def.turn) + lane * 0.9;
    if (t > def.turn) t = def.turn;
    if (t < -def.turn) t = -def.turn;
    const l = Math.sqrt(t * t + 1);
    return { ux: t / l, uz: -1 / l };
  }

  /** How far an island's (unwarped) coast reaches from its centre along unit direction (wx, wz). */
  islandExtent(isl: Island, wx: number, wz: number): number {
    const c = wx * isl.ux + wz * isl.uz;
    return isl.r * Math.sqrt(isl.e * isl.e * c * c + (1 - c * c));
  }

  /** Smooth, low-frequency field for island top heights. */
  private topField(x: number, z: number): number {
    const def = this.map.terrain.islands;
    return lerp(def.top[0], def.top[1], this.n.n2(x / 620 + 31, z / 620 - 17) * 0.5 + 0.5);
  }

  /** Make sure chain k reaches at least as far north as zMin. */
  private ensureChain(k: number, zMin: number): Island[] {
    const list = this.chainList(k);
    while (list[list.length - 1]!.cz > zMin) this.extendChain(k, list);
    return list;
  }

  /**
   * The real coast-to-coast gap from island a to island b, measured along the line between their
   * centres through the warped coastlines (≤ 0 when they touch).
   */
  coastGap(a: Island, b: Island): number {
    let dx = b.cx - a.cx;
    let dz = b.cz - a.cz;
    const L = Math.sqrt(dx * dx + dz * dz);
    if (L < 1e-6) return 0;
    dx /= L;
    dz /= L;
    const outside = (isl: Island, t: number): boolean => this.islandDistance(isl, a.cx + dx * t, a.cz + dz * t) >= isl.r;
    // Leave a…
    let t = 0;
    while (t < L && !outside(a, t)) t += 1;
    let lo = t - 1;
    let hi = t;
    for (let i = 0; i < 8; i++) {
      const m = (lo + hi) / 2;
      if (outside(a, m)) hi = m;
      else lo = m;
    }
    const exit = hi;
    if (!outside(b, exit)) return 0;
    // …and reach b.
    t = exit;
    while (t < L + b.r * b.e * 2 && outside(b, t)) t += 1;
    lo = t - 1;
    hi = t;
    for (let i = 0; i < 8; i++) {
      const m = (lo + hi) / 2;
      if (outside(b, m)) lo = m;
      else hi = m;
    }
    return hi - exit;
  }

  /** The next island along a chain (generating it if needed). */
  nextInChain(isl: Island): Island {
    return this.ensureChain(isl.chain, isl.cz - 1)[isl.index + 1] ?? this.ensureChain(isl.chain, isl.cz - 400)[isl.index + 1]!;
  }

  /** Islands whose influence may reach (x, z). */
  islandsNear(x: number, z: number, out: Island[] = this.scratch, pad = 0): Island[] {
    out.length = 0;
    const def = this.map.terrain.islands;
    const S = def.chainSpacing;
    const reach = 175 + pad;
    // Branch islands can sit up to a hop outside their lane's meander: scan one extra chain.
    const k0 = Math.floor((x - reach - def.meander) / S) - 1;
    const k1 = Math.ceil((x + reach + def.meander) / S) + 1;
    for (let k = k0; k <= k1; k++) {
      const list = this.ensureChain(k, z - reach - 120);
      // Binary search: list is sorted by cz descending.
      let lo = 0;
      let hi = list.length - 1;
      const zHi = z + reach;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (list[mid]!.cz > zHi) lo = mid + 1;
        else hi = mid;
      }
      for (let i = lo; i < list.length; i++) {
        const isl = list[i]!;
        if (isl.cz < z - reach) break;
        const dx = isl.cx - x;
        const ext = isl.r * isl.e * 1.45 + 12 + pad;
        if (dx > ext || -dx > ext) continue;
        out.push(isl);
      }
    }
    return out;
  }

  /** The island you're most "on" at (x, z), if any. */
  islandAt(x: number, z: number): Island | null {
    let best: Island | null = null;
    let bestU = Infinity;
    for (const isl of this.islandsNear(x, z, [])) {
      const u = this.islandDistance(isl, x, z) / isl.r;
      if (u < bestU) {
        bestU = u;
        best = isl;
      }
    }
    return bestU < 1.05 ? best : null;
  }

  /** Warped distance from an island's centre (shapes the coastline). */
  islandDistance(isl: Island, x: number, z: number): number {
    const dx = x - isl.cx;
    const dz = z - isl.cz;
    // Elliptical: stretched along the heading into a runway toward the next island.
    const along = (dx * isl.ux + dz * isl.uz) / isl.e;
    const across = dz * isl.ux - dx * isl.uz;
    const o = (isl.id & 1023) * 0.37;
    // Gentle, broad coastline warp (no ragged nibbles that eat a launch line).
    return Math.sqrt(along * along + across * across) + 5 * this.warpN.n2(x / 34 + o, z / 34 - o) + 1.1 * this.n.n2(x / 11 + o, z / 11);
  }

  // ---------------------------------------------------------------- ground

  /** Height and coverage at (x, z) without gradient. */
  ground(x: number, z: number, out: GroundSample): GroundSample {
    if (!this.islands) {
      out.h = this.swell(x, z, this.startCalm(x, z));
      out.mask = 1;
      return out;
    }
    const def = this.map.terrain.islands;
    const list = this.islandsNear(x, z);
    let wSum = 0;
    let hSum = 0;
    let mask = 0;
    for (let k = 0; k < list.length; k++) {
      const isl = list[k]!;
      const dist = this.islandDistance(isl, x, z);
      if (dist > isl.r * 1.4) continue;
      const u = dist / isl.r;
      const m = 1 - smooth3(saturate((dist - isl.r + 1.5) / 3));
      if (m > mask) mask = m;
      // Raised rim on the forward side (facing the next island): a ramp that launches you at it.
      // The ramp is still rising at the coastline, so it kicks you up and out. (Weaker on the flanks,
      // none on the south side where you arrive.)
      const fwd = ((x - isl.cx) * isl.ux + (z - isl.cz) * isl.uz) / Math.max(Math.sqrt((x - isl.cx) * (x - isl.cx) + (z - isl.cz) * (z - isl.cz)), 1e-3);
      const rim = smooth3(saturate((u - 0.8) / 0.26)) * isl.lip * saturate(fwd * 0.8 + 0.3);
      const hillKeep = 0.45 + 0.55 * (1 - smooth3(saturate((u - 0.55) / 0.4)));
      const surf = isl.top + this.swell(x, z, def.hillScale * hillKeep * this.startCalm(x, z)) + rim + (isl.chain === 0 && isl.index === 0 ? this.startRamp(x, z) : 0);
      const w = 1 - smooth3(saturate((u - 0.85) / 0.45)) + 1e-4;
      wSum += w;
      hSum += w * surf;
    }
    if (wSum === 0) {
      out.h = this.map.terrain.fallY - 30;
      out.mask = 0;
      return out;
    }
    out.h = hSum / wSum;
    out.mask = mask;
    return out;
  }

  height(x: number, z: number): number {
    return this.ground(x, z, this.out).h;
  }

  mask(x: number, z: number): number {
    return this.ground(x, z, this.out).mask;
  }

  /** Height, gradient (central differences) and coverage. */
  sample(x: number, z: number, out: GroundSample): GroundSample {
    const g = this.ground(x, z, this.out);
    out.h = g.h;
    out.mask = g.mask;
    const hxp = this.height(x + EPS, z);
    const hxm = this.height(x - EPS, z);
    const hzp = this.height(x, z + EPS);
    const hzm = this.height(x, z - EPS);
    out.hx = (hxp - hxm) / (2 * EPS);
    out.hz = (hzp - hzm) / (2 * EPS);
    return out;
  }

  /** Is (x, z) well inside solid ground (≥ `inset` m from any edge)? */
  solidInset(x: number, z: number, inset: number): boolean {
    if (!this.islands) return true;
    if (this.mask(x, z) < 0.999) return false;
    if (inset <= 0) return true;
    return (
      this.mask(x + inset, z) > 0.999 &&
      this.mask(x - inset, z) > 0.999 &&
      this.mask(x, z + inset) > 0.999 &&
      this.mask(x, z - inset) > 0.999
    );
  }
}
